import { Dispatch, SetStateAction } from 'react'
import { fetchData } from '../../../utils/fetch'

interface fetchDataAndSetTableProps<T> {
  endpoint: string
  queries: Record<string, string>[]
  setTableState: Dispatch<
    SetStateAction<{
      data: T[]
      isLoading: boolean
      pageSize: number
      offSet: number
      count: number
    }>
  >
  dataTransformer?: ({ items, count }: { items: T[]; count: number }) => T[]
}

const abortControllers = new Map<string, AbortController>()

/**
 * This function aims to replace the not type safe retrieveData function
 * It has a more flexible structure and can be used to make more queries
 * for the endpoints associated with the tables instead of relying upon
 * the more rigid structure, yet looser type safety of the retrieveData
 * function. Additionally, this does route our fetch requests through
 * the fetchData function, which has good logging and error handling.
 */
async function fetchDataAndSetTable<T>({
  endpoint,
  queries,
  setTableState,
  dataTransformer,
}: fetchDataAndSetTableProps<T>) {
  if (abortControllers.has(endpoint)) {
    abortControllers.get(endpoint)?.abort()
  }
  const abortController = new AbortController()
  abortControllers.set(endpoint, abortController)

  setTableState((prevState) => ({
    ...prevState,
    isLoading: true,
    data: [],
  }))

  let url = endpoint

  const nonEmptyQueries = queries.filter((query) => {
    const string = Object.values(query)[0]
    const isNotEmpty = !!string && string.length > 0
    return isNotEmpty
  })

  nonEmptyQueries.forEach((query, index) => {
    if (index === 0) {
      url += '?'
    } else {
      url += '&'
    }
    url += new URLSearchParams(query)
  })

  const signal = abortController.signal

  try {
    const response = await fetchData<{ items: T[]; count: number }>(
      { url, method: 'GET' },
      signal,
    )
    const items = dataTransformer ? dataTransformer(response) : response.items
    const count = response.count
    setTableState((prevState) => ({
      ...prevState,
      isLoading: false,
      data: items,
      count: count,
    }))
  } catch (error) {
    // Don't log abort errors - they're expected when requests are cancelled
    if (error instanceof Error && error.name === 'AbortError') {
      return
    }
    console.error(error)
    setTableState((prevState) => ({
      ...prevState,
      isLoading: false,
      data: [],
    }))
  }
}

export default fetchDataAndSetTable
